import { ref, watch } from 'vue'
import { useUIStore } from '@/stores/ui'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'
import { queryClient } from '@/shared/queryClient'

/**
 * Manages custom data fields and tags — both the summary counts shown
 * on the main page and the full lists shown inside the modal.
 */
export function useCustomDataTags() {
  const _uiStore = useUIStore()

  const total = ref({ tags: 0, customFields: 0 })
  const allTags = ref([])
  const allFields = ref([])
  const customDataLoading = ref(false)
  const openCustomDetailsModal = ref(false)
  const activeTab = ref('customData')

  // ── Private ──────────────────────────────────────────────────────────────────

  function _handleError(error) {
    _uiStore.updateToast({ open: true, message: error.message, type: 'danger' })
  }

  // ── Counts (shown on the main page — fetched once on mount) ─────────────────

  async function fetchTagsCount() {
    try {
      customDataLoading.value = true
      const response = await apiInterceptor.get(
        environment('VUE_APP_API_URL') + 'crm/v1/customers/tags/count',
      )
      total.value.tags = response?.count ?? 0
    } catch (error) {
      _handleError(error)
    } finally {
      customDataLoading.value = false
    }
  }

  async function fetchCustomFieldsCount() {
    try {
      customDataLoading.value = true
      const response = await apiInterceptor.get(
        environment('VUE_APP_API_URL') + 'crm/v1/customers/custom-fields/count',
      )
      total.value.customFields = response?.count ?? 0
    } catch (error) {
      _handleError(error)
    } finally {
      customDataLoading.value = false
    }
  }

  // ── Full lists (fetched lazily when the modal opens) ─────────────────────────

  async function fetchCustomTags() {
    try {
      const response = await apiInterceptor.get(
        environment('VUE_APP_API_URL') + 'crm/v1/customers/tags',
      )
      allTags.value = [...new Set(response.data.tags || [])]
    } catch (error) {
      _handleError(error)
    }
  }

  async function fetchCustomFields() {
    try {
      const response = await apiInterceptor.get(
        environment('VUE_APP_API_URL') + 'crm/v1/customers/custom-fields',
      )
      allFields.value = [...new Set(response.data.custom_fields || [])]
    } catch (error) {
      _handleError(error)
    }
  }

  /**
   * Opens the modal on the requested tab and lazily loads both datasets
   * in parallel so tab-switching never triggers a network request.
   */
  async function showAll(tab) {
    openCustomDetailsModal.value = true
    activeTab.value = tab === 'tags' ? 'tags' : 'customData'

    customDataLoading.value = true
    await Promise.all([fetchCustomFields(), fetchCustomTags()])
    customDataLoading.value = false
  }

  // Fallback lazy loader: if the modal opens without showAll (e.g. deep-link),
  // fetch whichever dataset is missing when the user switches tabs.
  watch(activeTab, (tab) => {
    if (!openCustomDetailsModal.value) return
    if (tab === 'tags' && !allTags.value.length) fetchCustomTags()
    else if (tab === 'customData' && !allFields.value.length) fetchCustomFields()
  })

  // ── Deletion ─────────────────────────────────────────────────────────────────

  async function _deleteAndRefresh(isTag, identifier) {
    try {
      _uiStore.updateLoading({ isLoading: true, message: 'Deleting...' })
      let response
      if (isTag) {
        response = await apiInterceptor.delete(
          environment('VUE_APP_API_URL') + 'crm/v1/customers/tags',
          { data: { tag: identifier } },
        )
        queryClient.invalidateQueries({ queryKey: ['customer-tags'] });
        await Promise.all([fetchTagsCount(), fetchCustomTags()])
      } else {
        response = await apiInterceptor.delete(
          environment('VUE_APP_API_URL') + 'crm/v1/customers/custom-fields',
          { data: { custom_field: identifier } },
        )
        queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
        await Promise.all([fetchCustomFieldsCount(), fetchCustomFields()])
      }
      _uiStore.updateToast({ open: true, message: response.message || response.apiMessage || "Successfully deleted", type: 'success' })
    } catch (error) {
      _handleError(error)
    } finally {
      _uiStore.updateLoading({ isLoading: false })
    }
  }

  function handleDeleteTag(tag) {
    _deleteAndRefresh(true, tag)
  }

  function handleDeleteField(field) {
    _deleteAndRefresh(false, field)
  }

  return {
    total,
    allTags,
    allFields,
    customDataLoading,
    openCustomDetailsModal,
    activeTab,
    fetchTagsCount,
    fetchCustomFieldsCount,
    showAll,
    handleDeleteTag,
    handleDeleteField,
  }
}
